import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { InitialsComponent } from '@app/core/components/initials/initials.component';
import { Events } from '@app/core/models/event-map';
import { AuthApiService } from '@app/core/services/auth-api.service';
import { PublishSubscribeService } from '@app/core/services/publish-subscribe.service';
import { CheckBoxComponent } from '@app/shared/components/check-box/check-box.component';
import { ContactSupportComponent } from '@app/shared/components/modals/contact-support/contact-support.component';
import { EstimatedDeliveryWeeksComponent } from '@app/shared/components/modals/estimated-delivery-weeks/estimated-delivery-weeks.component';
import { UserSetupComponent } from '@app/shared/components/settings/user-setup/user-setup.component';
import { Permission } from '@app/shared/models/access-control/user-permission.model';
import { PortalPermissionRole } from '@app/shared/models/portal-permission-role.model';
import { PortalUser } from '@app/shared/models/portal-user.model';
import { accessLevel } from '@app/shared/models/role-enum';
import { TopNodeType } from '@app/shared/models/salesbasket.enums';
import { CatalogueService } from '@app/shared/services/internal-services/catalogue-internal.service';
import { ConstantsService } from '@app/shared/services/internal-services/constants-internal.service';
import { PortalApiService } from '@app/shared/services/portal-api.service';
import { UserService } from '@app/shared/services/User.Service';
import { SetSelectedUser, SetShowMeasurements, SetShowPricesValue, SetShowUValue, SetShowVat } from '@app/shared/state/user-context/user-context.action';
import { UserContextSelectors } from '@app/shared/state/user-context/user-context.selectors';
import { InventoryBalanceModalComponent } from '@app/svf/components/modals/inventory-balance-modal/inventory-balance-modal.component';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { orderBy } from 'lodash-es';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from "primeng/overlaybadge";
import { TooltipModule } from 'primeng/tooltip';
import {
  filter,
  finalize,
  map
} from 'rxjs';



interface LeftNavPermission {
  area: string;
  action: string;
}

interface LeftNavItem {
  id?: string;
  label?: string;
  icon?: string;
  title?: string;
  badge?: string;
  visible?: boolean;
  separator?: boolean;
  permission?: LeftNavPermission;
  routePrefix?: string;
  children?: LeftNavItem[];
  action?: () => void;
  type?: 'toggle';
  checked?: boolean;
  toggleAction?: (checked: boolean) => void;
  elementId?: string;
}

@Component({
  selector: 'kp-left-nav',
  imports: [
    InitialsComponent,
    UserSetupComponent,
    ContactSupportComponent,
    EstimatedDeliveryWeeksComponent,
    CheckBoxComponent,
    RouterLink,
    TooltipModule,
    BadgeModule,
    OverlayBadgeModule,
    NgClass,
    InventoryBalanceModalComponent
],
  templateUrl: './left-nav.component.html',
  styleUrl: './left-nav.component.scss',
})
export class LeftNavComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly catalogueInternalService = inject(CatalogueService);
  private readonly modalService = inject(BsModalService);
  private readonly publishSubscribeService = inject(PublishSubscribeService);
  private readonly authService = inject(AuthApiService);

  isAuthenticated = this.store.selectSignal(UserContextSelectors.isAuthenticated);
  isMf = this.store.selectSignal(UserContextSelectors.isMf);
  isReseller = this.store.selectSignal(UserContextSelectors.isReseller);
  isAssembler = this.store.selectSignal(UserContextSelectors.isAssembler);
  isAftermarket = this.store.selectSignal(UserContextSelectors.isAftermarket);
  isCustAdmin = this.store.selectSignal(UserContextSelectors.isCustomerAdministrator);
  userFullName = this.store.selectSignal(UserContextSelectors.userFullname);
  userContext = this.store.selectSignal(UserContextSelectors.context)

  isTechnician = computed(() => {
    const perm = this.aftermarketPermission();
    const role = this.userRole();
    return perm?.value === 'TECHNICIAN' || role == accessLevel.AFTERMARKET_TECHNICIAN;
  });

  private aftermarketPermission = this.store.selectSignal(UserContextSelectors.hasPermission('Aftermarket', 'aftermarket_role'));
  private userRole = this.store.selectSignal(UserContextSelectors.userRole);

  readonly environment = environment.axEnv
  readonly isProduction = environment.production
  readonly environmentBadgeLabel = ({
    DEV_LOCAL: 'dev local',
    DEBUG_LOCAL: 'debug',
    D1_DEV: 'd1',
    D2_DEV: 'd2',
    MDEV_LOCAL: 'mdev local',
    ACC_LOCAL: 'tst local',
    DEV: 'dev',
    TEST: 'tst',
    ACC: 'tst',
    MDEV: 'mdev',
    MTEST: 'mtest'
  } as Record<string, string>)[environment.axEnv] ?? environment.axEnv;

  readonly newWebQuotationTitle = computed(() => `Skapa ${this.isMf() ? 'nytt affärsförslag' : 'ny webboffert'}`);
  readonly allowRoleChange = computed(() => this.isCustAdmin() && this.isMf());

  snickarAppNameUCFirst: string;
  snickarAppNameShort: string;

  readonly userProfileModalId = 'userProfileModalId';
  editUserProfileTemplate = viewChild<TemplateRef<void>>('editUserProfileTemplate');
  readonly contactSupportModalId = 'contactSupportModalId';
  contactSupportTemplate = viewChild<TemplateRef<void>>('contactSupportTemplate');

  readonly estimatedDeliveryWeeksModalId = 'estimatedDeliveryWeeksModalId';
  estimatedDeliveryWeeksTemplate = viewChild<TemplateRef<void>>('estimatedDeliveryWeeksTemplate');

  readonly inventoryBalanceModalId = 'inventoryBalanceModalId';
  inventoryBalanceTemplate = viewChild<TemplateRef<void>>('inventoryBalanceTemplate');

  leftNavPanel = viewChild<ElementRef<HTMLElement>>('leftNavPanel');

  currentRoute = signal('');

  openInlineItemId = signal<string | null>(null);
  activeFlyoutItemId = signal<string | null>(null);
  flyoutTop = signal(12);
  isAccountMenuOpen = signal(false);
  isSellerMenuOpen = signal(false);
  isRoleMenuOpen = signal(false);
  loadingRoleChange = signal(false);
  portalUsers = signal<PortalUser[]>([]);
  portalPermissionRoles = signal<PortalPermissionRole[]>([]);
  newRole = signal('');
  private flyoutCloseTimeoutId?: ReturnType<typeof setTimeout>;
  private accountMenuCloseTimeoutId?: ReturnType<typeof setTimeout>;

  portalUser: PortalUser = undefined;
  showPrices = this.store.selectSignal(UserContextSelectors.showPricesValue);
  showWithVAT = this.store.selectSignal(UserContextSelectors.showVat);
  showUValue = this.store.selectSignal(UserContextSelectors.showUValue);
  showMeasurements = this.store.selectSignal(UserContextSelectors.showMeasurements);

  sideMenuItems = computed<LeftNavItem[]>(() => {
    const isMf = this.isMf();
    const isReseller = this.isReseller();
    const addWebQuotationPermission: Permission = this.store.selectSnapshot(UserContextSelectors.hasPermission('Web-Quotation', 'add'));
    const informationPermission: Permission = this.store.selectSnapshot(UserContextSelectors.hasPermission('Portal', 'route'));
    const proffsportalPermission: Permission = this.store.selectSnapshot(UserContextSelectors.hasPermission('Resellers', 'route'));
    const adminPermission: Permission = this.store.selectSnapshot(UserContextSelectors.hasPermission('Portal', 'portal_admin'));
    const companySettingsPermission: Permission = this.store.selectSnapshot(UserContextSelectors.hasPermission('Admin-KP', 'manage_company'));
    const BackofficePermission: Permission = this.store.selectSnapshot(UserContextSelectors.hasPermission('Portal', 'backoffice'));

    const items: LeftNavItem[] = [
      // Skapa webboffert
      {
        id: 'NEW_QUOTATION',
        label: this.newWebQuotationTitle(),
        icon: 'fa-solid fa-lg fa-folder-plus',
        title: this.newWebQuotationTitle(),
        visible: !this.isTechnician() && !this.isAssembler(),
        permission: {
          area: addWebQuotationPermission.area,
          action: addWebQuotationPermission.action
        },
        routePrefix: '/webquotation/catalogue',
        action: () => this.onNewWebQuotation()
      },
      // Skapa reklamation
      {
        id: 'CREATE_RECLAIM',
        label: 'Skapa reklamation',
        icon: 'fa-duotone fa-lg fa-file-circle-exclamation',
        title: 'Skapa reklamation',
        visible: isMf,
        permission: {
          area: BackofficePermission.area,
          action: BackofficePermission.action
        },
        routePrefix: '/webquotation/catalogue',
        action: () => this.onNewWebQuotation()
      },
      // Startsidan
      {
        id: 'START',
        label: 'Hem',
        icon: 'fa-duotone fa-lg fa-home',
        title: 'Startsida',
        visible: true,
        routePrefix: '/start',
        action: () => this.router.navigate(['/start'])
      },
      // Kalender - Leveransveckor
      {
        id: 'DELIVERY_WEEKS',
        label: 'Leveransveckor',
        icon: 'fa-duotone fa-lg fa-calendar-week',
        title: 'Preliminära leveransveckor',
        visible: !this.isAssembler(),
        action: () => this.openModalEstimatedDlvWeeks()
      },
      {
        id: 'INVENTORY_BALANCE',
        label: 'Lagersaldo',
        icon: 'fa-duotone fa-lg fa-dolly',
        title: 'Lagersaldo',
        visible: !this.isMf(),
        action: () => this.openModalInventoryBalance()
      },
      // Statistik
      {
        id: 'STATISTICS',
        label: 'Statistik',
        icon: 'fa-duotone fa-lg fa-chart-mixed',
        visible: !this.isTechnician() && !this.isAssembler(),
        routePrefix: '/statistics',
        action: () => this.router.navigate(['/statistics'])
      },
      // iframe - Information
      {
        id: 'INFORMATION',
        label: 'Information',
        icon: 'fa-duotone fa-lg fa-info-circle',
        title: 'Information',
        visible: !this.isAftermarket(),
        permission: {
          area: informationPermission.area,
          action: informationPermission.action
        },
        routePrefix: '/information',
        action: () => {
          if(this.isMf()) {
            // currently mf has a sharepoint site as 'information' page and it cant be iframed.
            window.open(environment.iframeInformationMf, '_blank');
          } else {
            this.router.navigate(['/information'])
          }
        }
      },
      // Admin/Portaladministration
      {
        id: 'PORTAL_ADMIN',
        label: 'Portal admin',
        icon: 'fa-duotone fa-wrench',
        title: 'Administration',
        permission: {
          area: adminPermission.area,
          action: adminPermission.action
        },
        routePrefix: '/portalAdmin',
        action: () => this.router.navigate(['/portalAdmin'])
      },
      // Inställningar, kontakta support, notifieringar, statistik
      ...(!this.isAftermarket() ? [
        {
          id: 'HELP_SUPPORT',
          label: 'Kontakta support',
          icon: 'fa-duotone fa-lg fa-headset',
          action: () => this.openModalContactSupport()
        },
        // {
        //   id: 'HELP_NOTIFICATIONS',
        //   label: 'Notifieringar',
        //   icon: 'fa-duotone fa-lg fa-bell',
        //   routePrefix: '/notifications',
        //   action: () => this.router.navigate(['/notifications'])
        // },
        // Inställningar -> Submeny: Visa pris/moms/u-värde/mått, Företagsinställningar, Proffsportalen
        {
          id: 'SETTINGS',
          label: 'Inställningar',
          icon: 'fa-duotone fa-lg fa-head-side-gear',
          visible: !this.isTechnician() && !this.isAssembler(),
          title: '',
          children: [
            {
              id: 'SETTINGS_PRICES',
              label: 'Visa priser',
              type: 'toggle',
              checked: this.showPrices(),
              toggleAction: (checked: boolean) => this.setShowPrices(checked),
              elementId: 'LeftNavPriceSetting'
            },
            {
              id: 'SETTINGS_VAT',
              label: 'Visa inklusive moms',
              type: 'toggle',
              checked: this.showWithVAT(),
              toggleAction: (checked: boolean) => this.setShowVat(checked),
              elementId: 'LeftNavWithVATSetting'
            },
            {
              id: 'SETTINGS_UVALUE',
              label: 'Visa U-värde',
              type: 'toggle',
              checked: this.showUValue(),
              toggleAction: (checked: boolean) => this.setShowUValue(checked),
              elementId: 'LeftNavUValueSetting'
            },
            {
              id: 'SETTINGS_MEASUREMENTS',
              label: 'Visa mått i bilder',
              type: 'toggle' as const,
              checked: this.showMeasurements(),
              visible: isMf,
              toggleAction: (checked: boolean) => this.setShowMeasurements(checked),
              elementId: 'LeftNavMeasurementsSetting'
            },
            {
              id: 'SETTINGS_COMPANY',
              label: 'Företagsinställningar',
              icon: 'fa-duotone fa-lg fa-briefcase',
              permission: {
                area: companySettingsPermission.area,
                action: companySettingsPermission.action
              },
              routePrefix: '/custSetup',
              action: () => this.router.navigate(['/custSetup'])
            },
            {
              id: 'PROFFS_PORTAL',
              label: isReseller ? this.snickarAppNameShort : this.snickarAppNameUCFirst,
              icon: 'fa-duotone fa-lg fa-hammer-brush',
              title: '',
              visible: !isMf,
              permission: {
                area: proffsportalPermission.area,
                action: proffsportalPermission.action
              },
              routePrefix: '/proffsportalen-users',
              action: () => this.router.navigate(['/proffsportalen-users'], { queryParams: { status: 'Approved' }}),
            },
          ]
        } as LeftNavItem,
      ] : [])
    ];

    return items
      .filter((item) => item.visible !== false)
      .filter((item) => this.hasMenuPermission(item))
      .map((item) => ({
        ...item,
        children: item.children?.filter((child) => this.hasMenuPermission(child))
      }));
  });

   selectedUser = computed(() => {
    const user = this.userContext();

    if (!user?.selectedUser) {
      return null;
    }

    return user.axUserId !== user.selectedUser.USERID
      ? user.selectedUser
      : null;
  });

  activeFlyoutItem = computed(() => {
    const activeId = this.activeFlyoutItemId();
    if (!activeId) {
      return null;
    }

    return this.sideMenuItems().find((item) => item.id === activeId) ?? null;
  });

  readonly dSignal = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map(params => params.get('d'))
    ),
    { initialValue: null }
  );

  addMfDomainParam() {
    if(this.reloadForMfStyles()) {
      const url = new URL(window.location.href);
      url.searchParams.set('d', 'mf');
      window.location.replace(url.toString());
    }
  }

  reloadForMfStyles = computed(() => {
    const isMf = this.isMf()
    const params = this.dSignal()
    const isLocalhost = window.location.hostname === 'localhost'
    return isMf && params !== 'mf' && isLocalhost
  })

  constructor(
    private userInternalService: UserService,
    private router: Router,
    private portalApiService: PortalApiService
  ) {
    this.snickarAppNameUCFirst = environment.snickarAppNameUCFirst;
    this.snickarAppNameShort = environment.snickarAppNameShort;
    this.snickarAppNameShort =
      this.snickarAppNameShort == null || this.snickarAppNameShort == ''
        ? 'Mobilappen'
        : this.snickarAppNameShort;
  }

  ngOnInit(): void {
    this.currentRoute.set(this.router.url);
    this.registerEventSubscriptions();
    this.setData();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.currentRoute.set(event.urlAfterRedirects);
        this.closeMenus();
      });

    this.userInternalService.portalUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user: PortalUser) => {
          if (user) {
            this.portalUser = user;
            this.handleEventUserLoggedInOrOut();
          }
        }
      });
  }

  private registerEventSubscriptions(): void {
    this.publishSubscribeService
        .on(Events.UserService.USER_LOGGED_OUT)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.handleEventUserLoggedInOrOut();
          },
        });

    this.publishSubscribeService
        .on(Events.UserService.PORTAL_USERS_CHANGED)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.setData()
        });

    this.publishSubscribeService
        .on(Events.UserService.USER_LOGGED_IN)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.setData()
        });

    this.publishSubscribeService
        .on(Events.UserService.SET_PORTAL_PERMISSION_ROLE)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (event: { role: string }) => {
            if (event?.role) {
              this.newRole.set(event.role);
            }
          }
        });
  }

  isMainMenuMinimized() {
    const wrapper = document.getElementById('wrapper');
    return wrapper === null ? true : wrapper.classList.contains('toggled');
  }

  hasChildren(item: LeftNavItem): boolean {
    return !!item.children?.length;
  }

  isInlineExpanded(item: LeftNavItem): boolean {
    return this.openInlineItemId() === item.id;
  }

  isItemActive(item: LeftNavItem): boolean {
    if (item.routePrefix && this.isRouteActive(item.routePrefix)) {
      return true;
    }

    return item.children?.some((child) => this.isItemActive(child)) ?? false;
  }

  onItemMouseEnter(item: LeftNavItem, event: MouseEvent): void {
    this.cancelFlyoutClose();

    if (!this.isMainMenuMinimized() || !this.hasChildren(item)) {
      return;
    }

    this.openFlyout(item, event.currentTarget as HTMLElement);
  }

  onItemTrigger(item: LeftNavItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasChildren(item)) {
      this.isAccountMenuOpen.set(false);

      if (this.isMainMenuMinimized()) {
        this.openFlyout(item, event.currentTarget as HTMLElement);
      } else {
        this.openInlineItemId.update((currentId) => currentId === item.id ? null : item.id ?? null);
      }

      return;
    }

    this.activateItem(item);
  }

  onFlyoutItemClick(item: LeftNavItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.activateItem(item);
  }

  onToggleItem(item: LeftNavItem, checked: boolean): void {
    item.toggleAction?.(checked);
  }

  onPanelMouseLeave(): void {
    this.scheduleFlyoutClose();
    this.scheduleAccountMenuClose();
  }

  /**
   * Pekskärm har ingen mouseleave att stänga på; ett tryck utanför panelen stänger
   * utfällningen och kontomenyn. Påverkar inte mus, där mouseleave redan har stängt dem.
   */
  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    if (!this.activeFlyoutItemId() && !this.isAccountMenuOpen()) {
      return;
    }

    const panel = this.leftNavPanel()?.nativeElement;
    if (panel && !panel.contains(event.target as Node)) {
      this.cancelFlyoutClose();
      this.cancelAccountMenuClose();
      this.activeFlyoutItemId.set(null);
      this.isAccountMenuOpen.set(false);
      this.isSellerMenuOpen.set(false);
      this.isRoleMenuOpen.set(false);
    }
  }

  toggleAccountMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.cancelFlyoutClose();
    this.cancelAccountMenuClose();
    this.activeFlyoutItemId.set(null);
    this.isAccountMenuOpen.update((open) => !open);

    if (!this.isAccountMenuOpen()) {
      this.isSellerMenuOpen.set(false);
      this.isRoleMenuOpen.set(false);
    }
  }

  toggleSellerMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.cancelAccountMenuClose();
    this.isSellerMenuOpen.update((open) => !open);
    if (this.isSellerMenuOpen()) {
      this.isRoleMenuOpen.set(false);
    }
  }

  toggleRoleMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.cancelAccountMenuClose();
    this.isRoleMenuOpen.update((open) => !open);
    if (this.isRoleMenuOpen()) {
      this.isSellerMenuOpen.set(false);
    }
  }

  scheduleFlyoutClose(): void {
    this.cancelFlyoutClose();
    this.flyoutCloseTimeoutId = setTimeout(() => {
      this.activeFlyoutItemId.set(null);
    }, 120);
  }

  cancelFlyoutClose(): void {
    if (this.flyoutCloseTimeoutId) {
      clearTimeout(this.flyoutCloseTimeoutId);
      this.flyoutCloseTimeoutId = undefined;
    }
  }

  scheduleAccountMenuClose(): void {
    this.cancelAccountMenuClose();
    this.accountMenuCloseTimeoutId = setTimeout(() => {
      this.isAccountMenuOpen.set(false);
      this.isSellerMenuOpen.set(false);
      this.isRoleMenuOpen.set(false);
    }, 120);
  }

  cancelAccountMenuClose(): void {
    if (this.accountMenuCloseTimeoutId) {
      clearTimeout(this.accountMenuCloseTimeoutId);
      this.accountMenuCloseTimeoutId = undefined;
    }
  }

  private openFlyout(item: LeftNavItem, triggerElement: HTMLElement): void {
    if (!item.id) {
      return;
    }

    this.isAccountMenuOpen.set(false);

    const panelElement = this.leftNavPanel()?.nativeElement;
    if (panelElement) {
      const panelRect = panelElement.getBoundingClientRect();
      const triggerRect = triggerElement.getBoundingClientRect();
      this.flyoutTop.set(Math.max(12, triggerRect.top - panelRect.top));
    }

    this.activeFlyoutItemId.set(item.id);
  }

  private closeMenus(): void {
    this.cancelFlyoutClose();
    this.cancelAccountMenuClose();
    this.activeFlyoutItemId.set(null);
    this.openInlineItemId.set(null);
    this.isAccountMenuOpen.set(false);
    this.isSellerMenuOpen.set(false);
    this.isRoleMenuOpen.set(false);
  }

  private activateItem(item: LeftNavItem): void {
    item.action?.();
    this.closeMenus();
  }

  openModalEditUserDialog(): void {
    const template = this.editUserProfileTemplate();
    if (!template) {
      return;
    }

    this.modalService.show(template, { id: this.userProfileModalId, class: 'modal-lg' });
    this.closeMenus();
  }

  openModalContactSupport(): void {
    const template = this.contactSupportTemplate();
    if (!template) {
      return;
    }

    this.modalService.show(template, { id: this.contactSupportModalId, class: 'modal-md' });
    this.closeMenus();
  }

  openModalEstimatedDlvWeeks(): void {
    const template = this.estimatedDeliveryWeeksTemplate();
    if (!template) {
      return;
    }

    this.modalService.show(template, { id: this.estimatedDeliveryWeeksModalId, class: 'modal-lg' });
    this.closeMenus();
  }

  openModalInventoryBalance(): void {
    const template = this.inventoryBalanceTemplate();
    if (!template) {
      return;
    }

    this.modalService.show(template, { id: this.inventoryBalanceModalId, class: 'modal-lg' });
    this.closeMenus();
  }

  setSelectedPortalUser(portalUser: PortalUser): void {
    this.store.dispatch(new SetSelectedUser(portalUser.NETWORKALIAS, portalUser.NETWORKDOMAIN));
    this.userInternalService.setSelectedPortalUser(portalUser);
    this.closeMenus();
  }

  setShowPrices(checked: boolean): void {
    this.store.dispatch(new SetShowPricesValue(checked));
    this.userInternalService.setShowPrices(checked);
  }

  setShowVat(checked: boolean): void {
    this.store.dispatch(new SetShowVat(checked));
  }

  setShowUValue(checked: boolean): void {
    this.store.dispatch(new SetShowUValue(checked));
  }

  setShowMeasurements(checked: boolean): void {
    this.store.dispatch(new SetShowMeasurements(checked));
  }

  setPortalPermissionRoles(newRole: string): void {
    this.newRole.set(newRole);
    this.loadingRoleChange.set(true);

    this.portalApiService.setPortalPermissionRole(newRole)
      .pipe(finalize(() => this.loadingRoleChange.set(false)))
      .subscribe({
        next: (response) => {
          if (response?.Message) {
            this.publishSubscribeService.trigger(Events.UserService.SET_PORTAL_PERMISSION_ROLE, { role: response.Message });
          }
        },
        error: (err) => { console.error('setPortalPermissionRole', err?.message ?? err?.error); }
      });
  }

  logout(): void {
    this.closeMenus();
    this.authService.logout(undefined, false);
  }

  private isRouteActive(routePrefix: string): boolean {
    const currentRoute = this.currentRoute();
    return currentRoute === routePrefix || currentRoute.startsWith(`${routePrefix}/`);
  }

  onNewWebQuotation() {
    const preSelectedBrand = this.userInternalService.getPreSelectedBrand();
    const newWebQuotationStartsAt = this.userInternalService.getNewWebQuotationStartsAt();

    if (preSelectedBrand && newWebQuotationStartsAt === ConstantsService.NewWebQuotationStartsAt.ENUM_EMPTY_WEB_QUOTATION) {
      this.catalogueInternalService.createSalesBasket(preSelectedBrand, this.isMf() ? undefined : TopNodeType.Blank)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            const axResponse = response;

            if (axResponse.AllOk) {
              this.router
                .navigate([`/start`], { replaceUrl: false })
                .then(() => {
                  this.router.navigate(
                    [`/webquotation-v2`, axResponse.SalesBasketId],
                    { replaceUrl: true }
                  );
                });
            }
          },
          error: () => {
            this.router.navigate(['webquotation', 'catalogue', 0]);
          },
        });
    } else if (
      newWebQuotationStartsAt === ConstantsService.NewWebQuotationStartsAt.ENUM_SELECT_FIRST_ITEM
    ) {
      this.router.navigate(['webquotation', 'catalogue', 0]);
    }
  }

  private handleEventUserLoggedInOrOut() {
    if (this.isAuthenticated() && !this.isAssembler()) {
      this.setData();
    }
  }

  private getPortalPermissionRoles(): void {
    if (this.portalPermissionRoles().length > 0) {
      return;
    }

    this.portalApiService.getPortalPermissionRole()
      .subscribe({
        next: (response) => {
          if (response) {
            this.portalPermissionRoles.set(response);
          }
        },
        error: (err) => {
          console.log('Fetch_PortalPermissionRole', JSON.stringify(err));
        }
      });
  }

  private setData(): void {
    if (this.store.selectSnapshot(UserContextSelectors.isAuthenticated)) {
      this.portalUsers.set(orderBy(this.userInternalService.getPortalUsers().filter((u) => u?.USERFULLNAME), ['USERFULLNAME']));
      this.getPortalPermissionRoles();
    }
  }

  private hasMenuPermission(item: LeftNavItem): boolean {
    const permission = item.permission;

    if (!permission) {
      return true;
    }

    const acl = this.store.selectSnapshot(UserContextSelectors.permissionsAreaMap);
    const accessKey = `${permission.area}::${permission.action}`;
    const accessLevel = acl?.[accessKey]?.accesslevel ?? 0;

    return accessLevel > 0;
  }

  ngOnDestroy(): void {
    this.cancelFlyoutClose();
    this.cancelAccountMenuClose();
  }
}
